// Tabela HTML pura de lançamentos (não usa antd Table). Suporta:
// - checkbox de seleção em massa (header indeterminate)
// - drag-drop de colunas (handler ⋮⋮ no header)
// - sort por coluna (click no label)
// - destaque de linha (vencida = bg vermelho claro, selecionada = bg azul claro)
//
// COL_DEFS_CP fica no pai porque tem closures grandes sobre state setters
// (setCategoriaFiltro pra clicar em tag, abrirDetalhe pra Ver, etc).
// Extraído de FinanceiroContasPagar.tsx em 2026-05-23 (frontend wave 3B).
import { useState } from 'react';
import { Card, Empty } from 'antd';
import {
  ContaPagar, ColunaKeyCP, SortDir, COLUNAS_SORTAVEIS_CP,
} from './types';

export type ColDef = {
  label: string;
  align?: 'left' | 'right' | 'center';
  render: (c: ContaPagar) => React.ReactNode;
};

export function TabelaLancamentosCP({
  loading, ordenadas, ordemColunas, colDefs, selecionados,
  ordenacao, aplicarSort, onReordenarColuna, keySelecao,
  toggleSelecao, selecionarTodos, onAbrirDetalhe,
}: {
  loading: boolean;
  ordenadas: ContaPagar[];
  ordemColunas: ColunaKeyCP[];
  colDefs: Record<ColunaKeyCP, ColDef>;
  selecionados: Set<string>;
  ordenacao: { coluna: ColunaKeyCP; direcao: SortDir };
  aplicarSort: (k: ColunaKeyCP) => void;
  onReordenarColuna: (from: ColunaKeyCP, to: ColunaKeyCP) => void;
  keySelecao: (c: ContaPagar) => string;
  toggleSelecao: (c: ContaPagar) => void;
  selecionarTodos: (checked: boolean) => void;
  onAbrirDetalhe: (c: ContaPagar) => void;
}) {
  // dragColuna fica local: simplifica e não precisa ser exposto pro pai.
  const [dragColuna, setDragColuna] = useState<ColunaKeyCP | null>(null);

  if (loading) return <Card loading />;
  if (ordenadas.length === 0) return <Empty description="Nenhuma despesa com esses filtros." />;

  return (
    <Card size="small" bodyStyle={{ padding: 0 }} style={{ overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        {/* FIN-UI (2026-07-14): cabeçalho sticky — não some ao rolar */}
        <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#FAFBFF' }}>
          <tr style={{ background: '#FAFBFF' }}>
            {/* F-CP-bulk: checkbox "selecionar todos" */}
            <th style={{ padding: '8px 6px 8px 12px', width: 36, borderBottom: '0.5px solid #e0e0e0' }}
              onClick={(e) => e.stopPropagation()}>
              <input type="checkbox"
                checked={selecionados.size > 0 && selecionados.size === ordenadas.length}
                ref={(el) => { if (el) el.indeterminate = selecionados.size > 0 && selecionados.size < ordenadas.length; }}
                onChange={(e) => selecionarTodos(e.target.checked)} />
            </th>
            {ordemColunas.map((k) => {
              const def = colDefs[k];
              const arrastando = dragColuna === k;
              const sortavel = COLUNAS_SORTAVEIS_CP.includes(k);
              const ativoSort = ordenacao.coluna === k;
              return (
                <th key={k}
                  onClick={() => { if (!dragColuna && sortavel) aplicarSort(k); }}
                  style={{
                    padding: '8px 12px', textAlign: def.align || 'left',
                    fontSize: 10, color: '#555', fontWeight: 600,
                    borderBottom: '0.5px solid #e0e0e0', textTransform: 'uppercase',
                    letterSpacing: 0.4, cursor: sortavel ? 'pointer' : 'default',
                    background: arrastando ? '#D8E0FF' : ativoSort ? '#E8EEFF' : undefined,
                    userSelect: 'none', whiteSpace: 'nowrap',
                  }}>
                  <span draggable
                    onDragStart={(e) => { setDragColuna(k); e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
                    onDragOver={(e) => { if (dragColuna && dragColuna !== k) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                    onDrop={(e) => {
                      e.stopPropagation();
                      if (dragColuna && dragColuna !== k) onReordenarColuna(dragColuna, k);
                      setDragColuna(null);
                    }}
                    onDragEnd={() => setDragColuna(null)}
                    onClick={(e) => e.stopPropagation()}
                    title="Arraste pra reordenar"
                    style={{ opacity: 0.4, marginRight: 4, cursor: 'grab', display: 'inline-block', padding: '0 2px' }}>
                    ⋮⋮
                  </span>
                  {def.label}
                  {ativoSort && <span style={{ marginLeft: 4, color: '#0930D0', fontWeight: 700 }}>{ordenacao.direcao === 'asc' ? '↑' : '↓'}</span>}
                  {sortavel && !ativoSort && <span style={{ marginLeft: 4, color: '#bbb', fontSize: 9 }}>↕</span>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordenadas.map(c => {
            const isVencida = c.status === 'em_aberto' && c.data_vencimento && new Date(c.data_vencimento) < new Date();
            const checado = selecionados.has(keySelecao(c));
            return (
              <tr key={`${c.origem}-${c.id}`} onClick={() => onAbrirDetalhe(c)}
                style={{
                  borderBottom: '0.5px solid #f0f0f0',
                  background: checado ? '#E8EEFF' : isVencida ? '#FEF7F7' : 'transparent', cursor: 'pointer',
                }}>
                {/* F-CP-bulk: checkbox da linha */}
                <td style={{ padding: '10px 6px 10px 12px', verticalAlign: 'top' }}
                  onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={checado}
                    onChange={() => toggleSelecao(c)} />
                </td>
                {ordemColunas.map((k) => {
                  const def = colDefs[k];
                  return (
                    <td key={k} style={{ padding: '10px 12px', verticalAlign: 'top', textAlign: def.align || 'left' }}>
                      {def.render(c)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
